import React, { useState, useEffect } from 'react';
import Icon from '@components/AppIcon';
import Input from '@components/ui/Input';

const FAQSection = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openAccordion, setOpenAccordion] = useState(null);
  const [faqCategories, setFaqCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await fetch('/api/faqs');
        if (!response.ok) {
          throw new Error('Failed to fetch FAQs');
        }
        const data = await response.json();
        
        // Transform the API response to match the component's expected structure
        const transformedData = Object.entries(data).map(([title, faqs]) => ({
          title,
          faqs: faqs.map(faq => ({
            question: faq.question,
            answer: faq.answer
          }))
        }));
        
        setFaqCategories(transformedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const filteredCategories = faqCategories
    ?.map((category) => ({
      ...category,
      faqs: category?.faqs?.filter(
        (faq) =>
          faq?.question?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          faq?.answer?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      ),
    }))
    ?.filter((category) => category?.faqs?.length > 0);

  const toggleAccordion = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenAccordion(openAccordion === key ? null : key);
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-secondary/5 via-primary/5 to-accent/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
          <p>Loading FAQs...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-gradient-to-br from-secondary/5 via-primary/5 to-accent/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
          <p>Error loading FAQs: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-secondary/5 via-primary/5 to-accent/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Search Bar */}
        <div className="mb-10">
          <div className="relative max-w-md mx-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Search" size={20} className="text-text-secondary" />
            </div>
            <Input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="pl-10 shadow-sm border rounded-xl"
            />
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {filteredCategories?.map((category, categoryIndex) => (
            <div
              key={categoryIndex}
              className="bg-card border border-border rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center">
                <Icon name="HelpCircle" size={24} className="text-primary mr-3" />
                {category?.title}
              </h2>

              <div className="space-y-4">
                {category?.faqs?.map((faq, faqIndex) => {
                  const key = `${categoryIndex}-${faqIndex}`;
                  const isOpen = openAccordion === key;

                  return (
                    <div
                      key={faqIndex}
                      className="border border-border rounded-xl overflow-hidden transition-all duration-300"
                    >
                      <button
                        onClick={() => toggleAccordion(categoryIndex, faqIndex)}
                        className="w-full text-left p-4 bg-muted hover:bg-muted/80 flex items-center justify-between transition-colors"
                      >
                        <span className="font-semibold text-foreground pr-4">
                          {faq?.question}
                        </span>
                        <Icon
                          name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                          size={20}
                          className="text-text-secondary flex-shrink-0 transition-transform duration-300"
                        />
                      </button>

                      {isOpen && (
                        <div className="p-4 bg-card border-t border-border">
                          <p className="text-text-secondary leading-relaxed">
                            {faq?.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {searchTerm && filteredCategories?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-lg text-text-secondary">
              No FAQs found matching "{searchTerm}". Try a different search term.
            </p>
          </div>
        )}

        {/* No FAQs Available */}
        {!loading && faqCategories.length === 0 && (
          <div className="text-center py-12">
            <Icon name="HelpCircle" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-lg text-text-secondary">
              No FAQs available at the moment.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQSection;